// Clipboard read/write that works in BOTH the Tauri (wry / WKWebView) desktop
// build and the browser dev build.
//
// Why this exists: in the wry webview, `navigator.clipboard.readText()` is
// blocked (the page lacks clipboard-read permission), so the terminal's
// right-click "Paste" and Cmd+V silently no-op'd. The Tauri clipboard-manager
// plugin reads/writes the OS clipboard from the Rust side, bypassing the
// webview permission gate. We prefer it inside Tauri and fall back to the web
// Clipboard API in the browser (where the plugin isn't present).
//
// Static import mirrors services/folderPicker.ts: the plugin is tiny and a
// dynamic import gets code-split into a chunk Tauri's asset protocol sometimes
// fails to fetch.
import {
  readText as tauriReadText,
  writeText as tauriWriteText,
} from "@tauri-apps/plugin-clipboard-manager";

function inTauri(): boolean {
  return Boolean((window as unknown as { __TAURI_INTERNALS__?: unknown }).__TAURI_INTERNALS__);
}

export async function clipboardWriteText(text: string): Promise<void> {
  if (inTauri()) {
    try {
      await tauriWriteText(text);
      return;
    } catch {
      // Plugin call failed — fall through to the web API as a last resort.
    }
  }
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Clipboard unavailable (denied / missing); best-effort, swallow.
  }
}

export async function clipboardReadText(): Promise<string> {
  if (inTauri()) {
    try {
      const text = await tauriReadText();
      return text ?? "";
    } catch {
      // Plugin call failed — fall through to the web API.
    }
  }
  try {
    return (await navigator.clipboard.readText()) ?? "";
  } catch {
    // readText is blocked in wry and may be denied in browsers without a
    // gesture; return empty so callers no-op rather than throw.
    return "";
  }
}
